import React, { PropsWithChildren, useEffect, useState } from 'react';
import _ from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, BoxProps, Container, ContainerProps, Fab as FloatingActionButton, FabProps, Typography, TypographyProps } from '@mui/material';
import Grid, { Grid2Props } from '@mui/material/Grid2';
import { PageAndViewTitleStringAssets } from '../../../assets/stringAssets';
import GenericPageContainer from '../GenericPageContainer/GenericPageContainer';
import { styled } from '@mui/styles';
import { enumEducationalVideoItemType } from '../../../dataObjects/enums';
import fileBasedEducationalVideosCatalog_Json from '../../../assets/data/educationalVideosCatalog.json';
import { EducationalVideoItem, IEducationalVideoItem } from '../../../dataObjects/models/educationalVideos';
import { JsonConverter } from '../../../dataObjects/utilities/JsonConverter';
import { EducationalVideosVideoGridView } from '../../views/educationalVideos/EducationalVideosVideoGridView';
import { EducationalVideosRelatedCatalogGridView } from '../../views/educationalVideos/EducationalVideosRelatedCatalogGridView';
import { MenuBook as MainCatalogIcon } from '@mui/icons-material';
import { EducationalVideosStringAssets } from '../../../assets/stringAssets/EducationalVideosStringAssets';


/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/

// a styled Box (equivalent to a <div>), providing an area to display the overall header
const StyledBoxForPaddingAtopPage = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

// a styled Box for a Horizontal Divider (a CSS Horizontal Rule <hr/>)
const StyledBoxForHorizontalDivider = styled((props: BoxProps) => (
  <Box
    component='hr'
    {...props}
  />
))(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(4),
  borderTop: `1px solid ${theme.palette.primary.dark}`,
}));

// a styled Grid for the overall container of the 'To Main Catalog' Floating Action Button Area
const StyledGridForToMainCatalogButtonContainer = styled((props: Grid2Props) => (
  <Grid
    container
    {...props}
  />
))(({ theme }) => ({
  flexGrow: 1,
  width: "100%",
  justifyContent: 'center',
  marginTop: theme.spacing(-2), // we want to eliminate some top margin if the button is being displayed
}));

// a styled Floating Action Button
const StyledFloatingActionButton = styled((props: FabProps) => (
  <FloatingActionButton
    variant="extended"
    size="medium"
    color="primary"
    {...props}
  />
))(({ theme }) => ({
  // MuiFab theming isn't responding properly, so directly theme the backgroundColor upon hover
  '&:hover': {
    background: theme.floatingActionButton.hover.background,
    border: theme.floatingActionButton.hover.border,
    color: theme.floatingActionButton.hover.color,
  },
}));


// a styled Container for Data View
const StyledContainerForDataView = styled((props: ContainerProps) => (
  <Container
    disableGutters
    maxWidth={false}
    {...props}
  />
))(({ theme }) => ({
  flexGrow: 1,
  paddingLeft: 0,
  paddingRight: 0,
}));

// a styled Typography for a Section Header
const StyledTypographyForSectionHeader = styled((props: TypographyProps) => (
  <Typography
    variant='h5'
    {...props}
  />
))(({ theme }) => ({
  color: theme.page.titleFontColor,
  fontWeight: 'bold',
  marginBottom: theme.spacing(1),

  [theme.breakpoints.up('xs')]: {
    fontSize: '1.2rem',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.4rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.5rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.6rem',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '1.8rem',
  },

}));


export interface IEducationalVideosPageProps extends PropsWithChildren<unknown> {
  catalogId?: string,
}

const EducationalVideosPage: React.FC<IEducationalVideosPageProps> = (props: IEducationalVideosPageProps) => {

  EducationalVideosPage.displayName = 'Product Videos Page';

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = false;

  const params = useParams();

  const catalogId: string | undefined = params.catalogId; // from the path '/productVideos/:catalogId

  displayConsoleLogs && console.log(`EducationalVideosPage. catalogId: ${catalogId}`);

  // the entire Product Videos Catalog
  const [entireEducationalVideosCatalog, setEntireEducationalVideosCatalog] = useState<Array<IEducationalVideoItem>>([]);

  // the details for the selected Catalog
  const [selectedCatalog, setSelectedCatalog] = useState<IEducationalVideoItem | undefined>(undefined);
  // the Featured Videos associcated with the specified Catalog
  const [featuredVideos, setFeaturedVideos] = useState<Array<IEducationalVideoItem>>([]);
  // the Catalogs related to the specified Catalog
  const [relatedCatalogs, setRelatedCatalogs] = useState<Array<IEducationalVideoItem>>([]);
  // for using browser router navigate
  const navigate = useNavigate();


  // a useEffect hook that is executed upon mounting of the component
  useEffect(() => {
    displayConsoleLogs && console.log(`%c EducationalVideosPage, in useEffect upon mounting. Ready to read the Product Videos Catalog from a file.`, 'background: #600; color: #fff');

    const educationalVideosCatalog_fromJsonFile: Array<IEducationalVideoItem> = JsonConverter.arrayFromJSONArray(EducationalVideoItem, fileBasedEducationalVideosCatalog_Json);

    setEntireEducationalVideosCatalog(educationalVideosCatalog_fromJsonFile);
  }, []);

  // a useEffect hook that is executed upon a change to the entire catalog (collection)
  useEffect(() => {
    // if there's an unempty entire catalog...
    if (entireEducationalVideosCatalog.length > 0) {
      // if a catalogId has NOT been provided in the URL...
      if (catalogId === undefined) {
        // get the first record in the entire catalog, which should always be the main overall catalog
        const mainOverallCatalog: IEducationalVideoItem = entireEducationalVideosCatalog[0];

        // set that record as the selected catalog
        setSelectedCatalog(mainOverallCatalog);
      } else {
        // otherwise, find the related catalog item whose id matches the catalogId
        const matchingCatalog: IEducationalVideoItem | undefined = entireEducationalVideosCatalog.find(productVideoItem => productVideoItem.id === catalogId);

        // if we found the record whose id matches the catalogId...
        if (matchingCatalog) {
          // set that record as the selected catalog
          setSelectedCatalog(matchingCatalog);
        }
      }
    }
  }, [entireEducationalVideosCatalog, catalogId]);

  // a useEffect hook that is executed upon a change to the selected Catalog
  useEffect(() => {
    // if the selected catalog is a valid one...
    if (selectedCatalog) {
      // parse out the records for videos and subcatalogs
      const matchingEducationalVideoItems: Array<IEducationalVideoItem> = [];
      // iterate through the childIds of the selected catalog
      selectedCatalog.childIds.map(childId => {
        // try to find an item in the overall catalog that has an 'id' matching the current childId in the iteration
        const productVideoItem: IEducationalVideoItem | undefined = entireEducationalVideosCatalog.find(catalogItem => catalogItem.id === childId);
        // if an item was found...
        if (productVideoItem) {
          // add it to the array
          matchingEducationalVideoItems.push(productVideoItem);
        }
      })

      // now, divide the the found items into two arrays: Videos and Sub-Catalogs
      const videoItems: Array<IEducationalVideoItem> = matchingEducationalVideoItems.filter(item => item.itemType === enumEducationalVideoItemType.InformationalVideo || item.itemType === enumEducationalVideoItemType.TrainingVideo);
      const subCatalogItems: Array<IEducationalVideoItem> = matchingEducationalVideoItems.filter(item => item.itemType === enumEducationalVideoItemType.Catalog);

      // set the respective state variables
      setFeaturedVideos(videoItems);
      setRelatedCatalogs(subCatalogItems);
    }
  }, [selectedCatalog]);

  // Establish the page title to be displayed. 
  const pageTitle: string = PageAndViewTitleStringAssets.pageTitle_EducationalVideos;

  return (
    <>
      <StyledBoxForPaddingAtopPage />
      <GenericPageContainer
        // maxWidth="xl"
        showBackButton={true}
        pageTitle={pageTitle}
        pageSubtitle={selectedCatalog ? `Catalog: ${selectedCatalog.title}` : ''}
      >

        {(catalogId && (catalogId !== EducationalVideosStringAssets.mainCatalogId)) &&
          // if not currently at the Main Catalog, display a button that will allow the user to directly go to the Main Catalog
          <StyledGridForToMainCatalogButtonContainer>
            <StyledFloatingActionButton onClick={() => navigate(`/${EducationalVideosStringAssets.educationalVideosBaseRoute}/${EducationalVideosStringAssets.mainCatalogId}`)}>
              <MainCatalogIcon />
              &nbsp;{`To Main Catalog`}
            </StyledFloatingActionButton>
          </StyledGridForToMainCatalogButtonContainer>
        }

        {/* Container for embedded data view */}
        <StyledContainerForDataView>

          {/* if there are any "Featured Videos" for the current catalog, display the "Featured Videos" section */}
          {(featuredVideos.length > 0) &&
            <>
              <StyledTypographyForSectionHeader>
                {PageAndViewTitleStringAssets.viewSectionTitle_EducationalVideosFeaturedVideos}
              </StyledTypographyForSectionHeader>

              <EducationalVideosVideoGridView productVideos={featuredVideos} />

              <StyledBoxForHorizontalDivider />
            </>
          }

          {/* if there are any "Related Catalogs" for the current catalog, display the "Related Catalogs" section */}
          {(relatedCatalogs.length > 0) &&
            <>
              <StyledTypographyForSectionHeader>
                {PageAndViewTitleStringAssets.viewSectionTitle_EducationalVideosRelatedCatalogs}
              </StyledTypographyForSectionHeader>

              <EducationalVideosRelatedCatalogGridView relatedCatalogs={relatedCatalogs} />
            </>
          }

        </StyledContainerForDataView>

      </GenericPageContainer>
    </>
  );
}

export default EducationalVideosPage;
